import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtErr } from "../api";

const LEAVE_TYPES = ["Casual", "Sick", "Earned", "Unpaid"];

const StatusBadge = ({ value }) => {
  const cls = value === "Approved"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : value === "Rejected"
    ? "bg-red-50 text-[#C5221F] border-red-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{value}</span>;
};

const Leave = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ leave_type: "Casual", start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async () => {
    try {
      const { data } = await staffApi.get("/leaves/mine");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (form.end_date < form.start_date) {
      setFormError("End date cannot be before start date.");
      return;
    }
    setBusy(true);
    try {
      await staffApi.post("/leaves", form);
      toast.success("Leave request submitted.");
      setForm({ leave_type: "Casual", start_date: "", end_date: "", reason: "" });
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="staff-leave">
      <h1 className="font-display text-2xl font-bold text-slate-900">Leave</h1>
      <p className="mt-1 text-sm text-slate-500">Submit a leave request and track its status.</p>
      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6 lg:p-8" data-testid="leave-form-card">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">New Request</p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="lv-type" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Leave Type</label>
              <select
                id="lv-type" data-testid="leave-type-select"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]"
                value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
              >
                {LEAVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="lv-start" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">From</label>
                <input id="lv-start" type="date" required data-testid="leave-start-input"
                  className="w-full border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                  value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label htmlFor="lv-end" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">To</label>
                <input id="lv-end" type="date" required data-testid="leave-end-input"
                  className="w-full border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                  value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <label htmlFor="lv-reason" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Reason</label>
              <textarea id="lv-reason" required rows={3} data-testid="leave-reason-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="leave-form-error">{formError}</p>}
            <button type="submit" disabled={busy} data-testid="leave-submit-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors duration-150 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Submit Request
            </button>
          </form>
        </div>
        <div className="lg:col-span-3">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-4">My Leave History</p>
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
          ) : error ? (
            <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="leave-error">{error}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="leave-empty">No leave requests yet.</p>
          ) : (
            <ul className="space-y-3" data-testid="leave-list">
              {rows.map((l) => (
                <li key={l.id} className="bg-white border border-slate-200 p-5" data-testid={`leave-row-${l.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="font-display text-sm font-bold text-slate-900">{l.leave_type} Leave</p>
                    <StatusBadge value={l.status} />
                  </div>
                  <p className="mt-1.5 text-xs text-slate-500">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</p>
                  <p className="mt-2 text-sm text-slate-600">{l.reason}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leave;
